/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useAdminAuth } from '../../context/AdminAuthContext';
import axiosClient from '../../api/axiosClient';

// Brand accent color (matching BookingsNew.jsx)
const ACCENT = '#7d6633';

const ContactCard = ({ title, children }) => (
  <div className="rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl p-5">
    <h3 className="text-lg font-semibold mb-3 text-zinc-900 dark:text-zinc-100">{title}</h3>
    {children}
  </div>
);

const ContactSupport = () => {
  const { isDark } = useTheme();
  const { user } = useAdminAuth();
  const { t } = useTranslation('admin');

  // Form state
  const [formData, setFormData] = useState({
    businessName: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    category: '',
    subject: '',
    message: '',
    attachment: null
  });

  // UI state
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error'
  const [submitMessage, setSubmitMessage] = useState('');

  // Prefill form data from authenticated user
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        businessName: user.shop_name || prev.businessName,
        firstName: user.first_name || (user.name ? user.name.split(' ')[0] : '') || prev.firstName,
        lastName: user.last_name || (user.name ? user.name.split(' ').slice(1).join(' ') : '') || prev.lastName,
        email: user.email || prev.email,
        phone: user.phone || prev.phone
      }));
    }
  }, [user]);

  // Categories for the dropdown
  const categories = [
    t('admin.support_category_general'),
    t('admin.support_category_technical'),
    t('admin.support_category_booking'),
    t('admin.support_category_ai'),
    t('admin.support_category_billing'),
    t('admin.support_category_feature'),
    t('admin.support_category_other')
  ];

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle file upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      // Validate file type
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          attachment: t('admin.support_attachment_type_error')
        }));
        e.target.value = '';
        return;
      }

      // Validate file size (5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB in bytes
      if (file.size > maxSize) {
        setErrors(prev => ({
          ...prev,
          attachment: t('admin.support_attachment_size_error')
        }));
        e.target.value = '';
        return;
      }

      setFormData(prev => ({
        ...prev,
        attachment: file
      }));
      
      // Clear error
      setErrors(prev => ({
        ...prev,
        attachment: ''
      }));
    }
  };

  // Remove attachment
  const removeAttachment = () => {
    setFormData(prev => ({
      ...prev,
      attachment: null
    }));
    // Reset file input
    const fileInput = document.getElementById('attachment-input');
    if (fileInput) fileInput.value = '';
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.businessName.trim()) {
      newErrors.businessName = t('admin.support_error_business_name_required');
    }
    if (!formData.firstName.trim()) {
      newErrors.firstName = t('admin.support_error_first_name_required');
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = t('admin.support_error_last_name_required');
    }
    if (!formData.email.trim()) {
      newErrors.email = t('admin.support_error_email_required');
    } else {
      // Email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = t('admin.support_error_email_invalid');
      }
    }
    if (!formData.category) {
      newErrors.category = t('admin.support_error_category_required');
    }
    if (!formData.subject.trim()) {
      newErrors.subject = t('admin.support_error_subject_required');
    }
    if (!formData.message.trim()) {
      newErrors.message = t('admin.support_error_message_required');
    } else if (formData.message.trim().length < 20) {
      newErrors.message = t('admin.support_error_message_min');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous status
    setSubmitStatus(null);
    setSubmitMessage('');

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Create FormData for multipart/form-data submission
      const submitData = new FormData();
      submitData.append('businessName', formData.businessName);
      submitData.append('firstName', formData.firstName);
      submitData.append('lastName', formData.lastName);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone || '');
      submitData.append('category', formData.category);
      submitData.append('subject', formData.subject);
      submitData.append('message', formData.message);
      
      if (formData.attachment) {
        submitData.append('attachment', formData.attachment);
      }

      // Submit to backend
      const response = await axiosClient.post('/contact-support', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setSubmitStatus('success');
        setSubmitMessage(t('admin.support_submit_success'));
        
        // Reset form
        setFormData({
          businessName: user?.shop_name || '',
          firstName: user?.first_name || (user?.name ? user.name.split(' ')[0] : '') || '',
          lastName: user?.last_name || (user?.name ? user.name.split(' ').slice(1).join(' ') : '') || '',
          email: user?.email || '',
          phone: user?.phone || '',
          category: '',
          subject: '',
          message: '',
          attachment: null
        });
        
        // Reset file input
        const fileInput = document.getElementById('attachment-input');
        if (fileInput) fileInput.value = '';

        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }

    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitStatus('error');
      setSubmitMessage(
        error.response?.data?.message || 
        t('admin.support_submit_error')
      );
      
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">{t('admin.contact_support')}</h1>
        <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
          {t('admin.contact_support_subtitle')}
        </p>
      </div>

      {/* Success/Error Message */}
      {submitStatus && (
        <div className={`mb-6 p-4 rounded-lg ${
          submitStatus === 'success' 
            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' 
            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
        }`}>
          <p className={`text-sm ${
            submitStatus === 'success'
              ? 'text-green-800 dark:text-green-200'
              : 'text-red-800 dark:text-red-200'
          }`}>
            {submitMessage}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* Contact Form */}
          <ContactCard title={t('admin.contact_support_form_title')}>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Business & User Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                    {t('admin.support_business_name')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      errors.businessName 
                        ? 'border-red-500' 
                        : 'border-zinc-300 dark:border-zinc-700'
                    } bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#c9a038]`}
                    disabled={isSubmitting}
                  />
                  {errors.businessName && (
                    <p className="text-xs text-red-500 mt-1">{errors.businessName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                    {t('admin.support_first_name')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      errors.firstName 
                        ? 'border-red-500' 
                        : 'border-zinc-300 dark:border-zinc-700'
                    } bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#c9a038]`}
                    disabled={isSubmitting}
                  />
                  {errors.firstName && (
                    <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                    {t('admin.support_last_name')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      errors.lastName 
                        ? 'border-red-500' 
                        : 'border-zinc-300 dark:border-zinc-700'
                    } bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#c9a038]`}
                    disabled={isSubmitting}
                  />
                  {errors.lastName && (
                    <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                    {t('admin.email_address')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      errors.email 
                        ? 'border-red-500' 
                        : 'border-zinc-300 dark:border-zinc-700'
                    } bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#c9a038]`}
                    disabled={isSubmitting}
                  />
                  {errors.email && (
                    <p className="text-xs text-red-500 mt-1">{errors.email}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  {t('admin.support_phone_optional')}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#c9a038]"
                  disabled={isSubmitting}
                />
              </div>

              {/* Support Request Details */}
              <div className="pt-2">
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  {t('admin.support_category')} <span className="text-red-500">*</span>
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    errors.category 
                      ? 'border-red-500' 
                      : 'border-zinc-300 dark:border-zinc-700'
                  } bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#c9a038]`}
                  disabled={isSubmitting}
                >
                  <option value="">{t('admin.support_select_category')}</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                {errors.category && (
                  <p className="text-xs text-red-500 mt-1">{errors.category}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  {t('admin.support_subject')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    errors.subject 
                      ? 'border-red-500' 
                      : 'border-zinc-300 dark:border-zinc-700'
                  } bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#c9a038]`}
                  disabled={isSubmitting}
                />
                {errors.subject && (
                  <p className="text-xs text-red-500 mt-1">{errors.subject}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  {t('admin.message')} <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={6}
                  placeholder={t('admin.support_message_placeholder')}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    errors.message 
                      ? 'border-red-500' 
                      : 'border-zinc-300 dark:border-zinc-700'
                  } bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#c9a038] resize-none`}
                  disabled={isSubmitting}
                />
                <div className="flex justify-between items-center mt-1">
                  <div>
                    {errors.message && (
                      <p className="text-xs text-red-500">{errors.message}</p>
                    )}
                  </div>
                  <p className={`text-xs ${
                    formData.message.length < 20 
                      ? 'text-zinc-500 dark:text-zinc-400' 
                      : 'text-green-600 dark:text-green-400'
                  }`}>
                    {t('admin.support_message_count', { count: formData.message.length })}
                  </p>
                </div>
              </div>

              {/* Attachment */}
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                  {t('admin.support_attachment_optional')}
                </label>
                <div className="space-y-2">
                  {!formData.attachment ? (
                    <div>
                      <input
                        type="file"
                        id="attachment-input"
                        accept=".png,.jpg,.jpeg"
                        onChange={handleFileChange}
                        className="hidden"
                        disabled={isSubmitting}
                      />
                      <label
                        htmlFor="attachment-input"
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-medium cursor-pointer transition-all duration-200 hover:shadow-lg ${
                          isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        style={{ background: ACCENT }}
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        {t('admin.support_choose_file')}
                      </label>
                      <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                        {t('admin.support_attachment_hint')}
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-100 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700">
                      <div className="flex items-center gap-2">
                        <svg className="h-5 w-5 text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        <span className="text-sm text-zinc-700 dark:text-zinc-300">{formData.attachment.name}</span>
                        <span className="text-xs text-zinc-500">
                          ({(formData.attachment.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={removeAttachment}
                        className="text-red-500 hover:text-red-700"
                        disabled={isSubmitting}
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                  {errors.attachment && (
                    <p className="text-xs text-red-500">{errors.attachment}</p>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                  whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                  className={`w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-white text-base font-medium transition-all duration-200 hover:shadow-lg ${
                    isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  style={{ background: ACCENT }}
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('admin.support_sending')}
                    </>
                  ) : (
                    <>
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      {t('admin.support_send_message')}
                    </>
                  )}
                </motion.button>
              </div>
            </form>
          </ContactCard>

          {/* FAQ Section */}
          <ContactCard title={t('admin.support_faq_title')}>
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-1">
                  {t('admin.support_faq_1_q')}
                </h4>
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  {t('admin.support_faq_1_a')}
                </p>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-1">
                  {t('admin.support_faq_2_q')}
                </h4>
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  {t('admin.support_faq_2_a')}
                </p>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-1">
                  {t('admin.support_faq_3_q')}
                </h4>
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  {t('admin.support_faq_3_a')}
                </p>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-1">
                  {t('admin.support_faq_4_q')}
                </h4>
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  {t('admin.support_faq_4_a')}
                </p>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-1">
                  {t('admin.support_faq_5_q')}
                </h4>
                <p className="text-sm text-zinc-600 dark:text-zinc-400">
                  {t('admin.support_faq_5_a')}
                </p>
              </div>
            </div>
          </ContactCard>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          <ContactCard title={t('admin.support_hours_title')}>
            <p className="text-sm text-zinc-700 dark:text-zinc-300">{t('admin.support_hours_value')}</p>
          </ContactCard>

          <ContactCard title={t('admin.col_status')}>
            <p className="text-sm text-zinc-700 dark:text-zinc-300">{t('admin.all_systems_operational')}</p>
          </ContactCard>

          <ContactCard title={t('admin.support_email_title')}>
            <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-3">
              {t('admin.support_email_subtitle')}
            </p>
            <a
              href="mailto:support@hairchitech.com"
              className="inline-flex items-center gap-2 text-sm font-medium"
              style={{ color: '#c9a038' }}
            >
              support@hairchitech.com
            </a>
          </ContactCard>
        </div>
      </div>
    </div>
  );
};

export default ContactSupport;
